import {
  Component, OnInit, OnDestroy, ViewChild, ElementRef,
  AfterViewChecked, ChangeDetectorRef, NgZone
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatStateService } from '../../store/chat-state.service';
import { ChatState, ChatMessage, ChatAttachment, ChatSession } from '../../models/chat.models';
import { FeedbackEvent, ToolToggleEvent } from '../chat-message/chat-message.component';
import { SendEvent } from '../chat-input/chat-input.component';

@Component({
  selector: 'app-chat-window',
  // Default detection — safest for streaming
  template: `
    <div class="xcw-root">

      <!-- Header -->
      <div class="xcw-header">
        <!-- Sidebar toggle -->
        <button class="xcw-hbtn" (click)="toggleSidebar()" title="Conversations">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fill-rule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z" clip-rule="evenodd"/>
          </svg>
        </button>

        <div class="xcw-title">
          <span class="xcw-dot" [class.xcw-dot--streaming]="isStreaming"></span>
          <span class="xcw-title-text">{{ activeSessionTitle || 'XEDGE AI Assistant' }}</span>
        </div>

        <!-- New chat button — visible whenever a session is active -->
        <button
          *ngIf="activeSessionId"
          class="xcw-hbtn xcw-hbtn--new"
          (click)="newChat()"
          title="New conversation">
          <!-- compose / pencil-square icon -->
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor"
               stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
               width="16" height="16">
            <path d="M13.5 3.5L16.5 6.5M3 17h3l9.5-9.5a2.121 2.121 0 000-3L15 3a2.121 2.121 0 00-3 0L3 12.5V17z"/>
          </svg>
        </button>

        <div class="xcw-ctrl-badge" *ngIf="activeControllerId" title="Active controller">
          <span class="xcw-ctrl-dot"></span>{{ activeControllerId }}
        </div>

        <!-- Fullscreen toggle -->
        <button
          class="xcw-hbtn xcw-hbtn--fs"
          (click)="toggleFullscreen()"
          [title]="isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'">

          <!-- Expand icon (enter fullscreen) -->
          <svg *ngIf="!isFullscreen" viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
            <path fill-rule="evenodd"
              d="M3.28 2.22a.75.75 0 00-1.06 1.06l2.5 2.5H3a.75.75 0 000 1.5h3.5A.75.75 0 007.25 6.5V3a.75.75 0 00-1.5 0v1.72L3.28 2.22zM16.72 2.22a.75.75 0 011.06 1.06l-2.5 2.5H17a.75.75 0 010 1.5h-3.5a.75.75 0 01-.75-.75V3a.75.75 0 011.5 0v1.72l2.47-2.5zM3 13.25a.75.75 0 000 1.5h1.72l-2.5 2.47a.75.75 0 001.06 1.06l2.5-2.5V17a.75.75 0 001.5 0v-3.5a.75.75 0 00-.75-.75H3zM13.25 17a.75.75 0 001.5 0v-1.72l2.47 2.5a.75.75 0 001.06-1.06l-2.5-2.5H17a.75.75 0 000-1.5h-3.5a.75.75 0 00-.75.75V17z"
              clip-rule="evenodd"/>
          </svg>

          <!-- Compress icon (exit fullscreen) -->
          <svg *ngIf="isFullscreen" viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
            <path fill-rule="evenodd"
              d="M7.25 2.25a.75.75 0 00-1.5 0v1.72L3.28 1.47a.75.75 0 00-1.06 1.06l2.5 2.5H3a.75.75 0 000 1.5h3.5a.75.75 0 00.75-.75V2.25zM12.75 2.25a.75.75 0 011.5 0v1.72l2.47-2.5a.75.75 0 111.06 1.06l-2.5 2.5H17a.75.75 0 010 1.5h-3.5a.75.75 0 01-.75-.75V2.25zM3 12.75a.75.75 0 000 1.5h1.72l-2.5 2.47a.75.75 0 101.06 1.06l2.5-2.5V17a.75.75 0 001.5 0v-3.5a.75.75 0 00-.75-.75H3zM17 12.75a.75.75 0 010 1.5h-1.72l2.5 2.47a.75.75 0 11-1.06 1.06l-2.5-2.5V17a.75.75 0 01-1.5 0v-3.5a.75.75 0 01.75-.75H17z"
              clip-rule="evenodd"/>
          </svg>
        </button>

        <button class="xcw-hbtn xcw-hbtn--close" (click)="chatState.close()" title="Close">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/>
          </svg>
        </button>
      </div>

      <!-- ══ Session info bar — shown when a session is active ══ -->
      <div class="xcw-info-bar" *ngIf="activeSessionId && activeSession">

        <!-- Session title -->
        <span class="xcw-info-title" [title]="activeSession.title">{{ activeSession.title }}</span>

        <!-- Token counter (click for full breakdown tooltip) -->
        <div class="xcw-tok-wrap" *ngIf="activeSession.tokens?.total">
          <span class="xcw-tok-badge"
                [class.xcw-tok-badge--high]="(activeSession.tokens?.total || 0) > 500000">
            <!-- counter icon -->
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
                 stroke-width="1.4" stroke-linecap="round" width="11" height="11">
              <rect x="2" y="2" width="12" height="12" rx="2"/>
              <line x1="5" y1="8" x2="11" y2="8"/>
              <line x1="5" y1="5.5" x2="9" y2="5.5"/>
              <line x1="5" y1="10.5" x2="8" y2="10.5"/>
            </svg>
            in {{ formatNum(activeSession.tokens?.input || 0) }}
            <span class="xcw-tok-sep">·</span>
            out {{ formatNum(activeSession.tokens?.output || 0) }}
            <span class="xcw-tok-sep" *ngIf="(activeSession.tokens?.turns || 0) > 0">·</span>
            <span *ngIf="(activeSession.tokens?.turns || 0) > 0">{{ activeSession.tokens?.turns }} turns</span>
          </span>
          <!-- Hover tooltip -->
          <div class="xcw-tok-tooltip">
            <div class="xcw-tok-row"><span>Input tokens</span><strong>{{ (activeSession.tokens?.input || 0) | number }}</strong></div>
            <div class="xcw-tok-row"><span>Output tokens</span><strong>{{ (activeSession.tokens?.output || 0) | number }}</strong></div>
            <div class="xcw-tok-row"><span>Total tokens</span><strong>{{ (activeSession.tokens?.total || 0) | number }}</strong></div>
            <div class="xcw-tok-row"><span>Turns</span><strong>{{ activeSession.tokens?.turns || 0 }}</strong></div>
          </div>
        </div>

        <div class="xcw-info-actions">
          <!-- Release tokens -->
          <button
            class="xcw-info-btn"
            (click)="resetUsage()"
            title="Release tokens — zeros the token counter without deleting any messages. Useful for resetting cost tracking on a long-lived chat.">
            <!-- recycle icon -->
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
                 stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"
                 width="12" height="12">
              <path d="M2 8a6 6 0 016-6 6 6 0 014.24 1.76"/>
              <polyline points="10,2 14,2 14,6"/>
              <path d="M14 8a6 6 0 01-6 6 6 6 0 01-4.24-1.76"/>
              <polyline points="6,14 2,14 2,10"/>
            </svg>
            Release tokens
          </button>

          <!-- Compact -->
          <button
            class="xcw-info-btn xcw-info-btn--compact"
            (click)="compact()"
            title="Compact — summarises past messages into a single summary message, freeing context tokens for future turns. The original messages remain saved but are collapsed.">
            <!-- compress rows icon -->
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
                 stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"
                 width="12" height="12">
              <rect x="1" y="3" width="14" height="10" rx="2"/>
              <line x1="4" y1="7" x2="12" y2="7"/>
              <line x1="5.5" y1="9.5" x2="10.5" y2="9.5"/>
            </svg>
            Compact
          </button>
        </div>
      </div>

      <!-- Error Banner -->
      <div class="xcw-error" *ngIf="errorMsg">
        <svg viewBox="0 0 16 16" fill="currentColor" width="13" height="13">
          <path fill-rule="evenodd" d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0114.082 15H1.918a1.75 1.75 0 01-1.543-2.575L6.457 1.047zM9 11a1 1 0 11-2 0 1 1 0 012 0zm-.25-5.25a.75.75 0 00-1.5 0v2.5a.75.75 0 001.5 0v-2.5z" clip-rule="evenodd"/>
        </svg>
        <span>{{ errorMsg }}</span>
        <button (click)="chatState.clearError()">×</button>
      </div>

      <!-- Body -->
      <div class="xcw-body">

        <!-- Sidebar -->
        <div class="xcw-sidebar" *ngIf="sidebarOpen">
          <app-chat-sessions></app-chat-sessions>
        </div>

        <!-- Messages area -->
        <div class="xcw-msgs" #msgsContainer>

          <!-- Welcome screen — only when no session and no messages -->
          <div class="xcw-welcome" *ngIf="!activeSessionId && activeMessages.length === 0">
            <div class="xcw-orb">
              <svg viewBox="0 0 48 48" fill="none" stroke="white" stroke-width="1.5" width="28" height="28">
                <circle cx="24" cy="24" r="20"/>
                <path d="M16 20c0-2.21 3.582-4 8-4s8 1.79 8 4"/>
                <path d="M16 28c0 2.21 3.582 4 8 4s8-1.79 8-4"/>
                <circle cx="24" cy="24" r="3" fill="white"/>
              </svg>
            </div>
            <h3 class="xcw-welcome-title">XEDGE AI Assistant</h3>
            <p class="xcw-welcome-sub">Ask anything about your network devices, telemetry, alerts, and configurations.</p>
            <div class="xcw-chips">
              <button class="xcw-chip" *ngFor="let s of suggestions" (click)="sendSuggestion(s)">{{ s }}</button>
            </div>
          </div>

          <!-- Loading skeletons -->
          <div class="xcw-skels" *ngIf="loadingMessages">
            <div class="xcw-skel xcw-skel--user"></div>
            <div class="xcw-skel xcw-skel--ai"></div>
            <div class="xcw-skel xcw-skel--user xcw-skel--sm"></div>
            <div class="xcw-skel xcw-skel--ai xcw-skel--lg"></div>
          </div>

          <!-- Messages — trackBy id ensures each bubble is its own DOM node -->
          <ng-container *ngIf="!loadingMessages">
            <app-chat-message
              *ngFor="let msg of activeMessages; trackBy: trackById"
              [message]="msg"
              (feedback)="onFeedback($event)"
              (toolToggle)="onToolToggle($event)">
            </app-chat-message>
          </ng-container>

          <div #scrollAnchor></div>
        </div>
      </div>

      <!-- Input -->
      <app-chat-input
        (messageSent)="onSend($event)"
        (stopStream)="chatState.stopStream()">
      </app-chat-input>
    </div>
  `,
  styles: [`
    .xcw-root { display: flex; flex-direction: column; height: 100%; background: #f7f9fc; border-radius: inherit; overflow: hidden; }

    .xcw-header { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: #fff; border-bottom: 1px solid #e8edf5; min-height: 52px; flex-shrink: 0; }
    .xcw-title { flex: 1; display: flex; align-items: center; gap: 8px; overflow: hidden; min-width: 0; }
    .xcw-title-text { font-size: 13.5px; font-weight: 600; color: #1e2d50; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .xcw-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: #22c55e; transition: background 0.3s; }
    .xcw-dot--streaming { background: #4f7ef7; animation: xcDotPulse 1s ease-in-out infinite; }
    @keyframes xcDotPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.45); opacity: 0.65; } }

    .xcw-ctrl-badge { display: flex; align-items: center; gap: 4px; background: #f0f4fc; border: 1px solid #d0d8f0; border-radius: 999px; padding: 3px 9px; font-size: 10.5px; font-weight: 600; color: #4f7ef7; white-space: nowrap; flex-shrink: 0; }
    .xcw-ctrl-dot { width: 5px; height: 5px; border-radius: 50%; background: #22c55e; flex-shrink: 0; }

    .xcw-hbtn { background: none; border: none; cursor: pointer; padding: 6px; color: #9ca3af; border-radius: 8px; display: flex; align-items: center; transition: color 0.14s, background 0.14s; flex-shrink: 0; }
    .xcw-hbtn:hover { color: #4f7ef7; background: #f0f4fc; }
    .xcw-hbtn--close:hover { color: #dc2626; }
    .xcw-hbtn--new { color: #6366f1; }
    .xcw-hbtn--new:hover { color: #4f46e5; background: #eef2ff; }
    .xcw-hbtn--fs:hover { color: #6366f1; background: #eef2ff; }

    /* ── Session info bar ── */
    .xcw-info-bar {
      display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
      padding: 6px 14px;
      background: #f8fafc;
      border-bottom: 1px solid #e8edf5;
      flex-shrink: 0; min-height: 36px;
    }
    .xcw-info-title {
      font-size: 12px; font-weight: 600; color: #2d3a5e;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      max-width: 180px; flex-shrink: 1;
    }

    /* Token badge + hover tooltip */
    .xcw-tok-wrap { position: relative; display: inline-flex; align-items: center; }
    .xcw-tok-badge {
      display: inline-flex; align-items: center; gap: 4px;
      background: #eef2ff; border: 1px solid #c7d2fe;
      border-radius: 999px; padding: 2px 9px;
      font-size: 11px; font-weight: 500; color: #4f46e5;
      white-space: nowrap; cursor: default;
      font-variant-numeric: tabular-nums;
    }
    .xcw-tok-badge--high { background: #fff7ed; border-color: #fed7aa; color: #c2410c; }
    .xcw-tok-sep { color: #a5b4fc; margin: 0 1px; }

    .xcw-tok-tooltip {
      display: none;
      position: absolute;
      top: calc(100% + 7px); left: 0;
      background: #1e2d50; color: #f1f5f9;
      border-radius: 10px; padding: 10px 14px;
      font-size: 11.5px; z-index: 9999;
      box-shadow: 0 6px 20px rgba(0,0,0,.28);
      min-width: 190px; flex-direction: column; gap: 5px;
      pointer-events: none;
    }
    .xcw-tok-tooltip::before {
      content: ''; position: absolute;
      bottom: 100%; left: 18px;
      border: 5px solid transparent; border-bottom-color: #1e2d50;
    }
    .xcw-tok-row { display: flex; justify-content: space-between; gap: 16px; }
    .xcw-tok-row span { color: #94a3b8; }
    .xcw-tok-row strong { font-variant-numeric: tabular-nums; }
    .xcw-tok-wrap:hover .xcw-tok-tooltip { display: flex; }

    /* Info action buttons */
    .xcw-info-actions { display: flex; align-items: center; gap: 5px; margin-left: auto; flex-shrink: 0; }
    .xcw-info-btn {
      display: inline-flex; align-items: center; gap: 5px;
      background: #fff; border: 1px solid #e2e8f0;
      border-radius: 7px; padding: 4px 10px;
      font-size: 11px; font-weight: 500; color: #475569;
      cursor: pointer; white-space: nowrap;
      transition: background .13s, border-color .13s, color .13s;
    }
    .xcw-info-btn:hover { background: #f0f4fc; border-color: #c7d2fe; color: #6366f1; }
    .xcw-info-btn--compact:hover { background: #ecfeff; border-color: #a5f3fc; color: #0891b2; }

    .xcw-error { display: flex; align-items: center; gap: 7px; padding: 7px 14px; background: #fff1f0; border-bottom: 1px solid #f8ca92; font-size: 12.5px; color: #e08c25; flex-shrink: 0; }
    .xcw-error button { margin-left: auto; background: none; border: none; cursor: pointer; color: #f6a542; font-size: 15px; }

    .xcw-body { flex: 1; display: flex; overflow: hidden; min-height: 0; }
    .xcw-sidebar { flex-shrink: 0; }

    .xcw-msgs { flex: 1; overflow-y: auto; padding: 10px 0 4px; display: flex; flex-direction: column; min-width: 0; }
    .xcw-msgs::-webkit-scrollbar { width: 5px; }
    .xcw-msgs::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 99px; }

    .xcw-welcome { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 28px 20px; text-align: center; gap: 12px; }
    .xcw-orb { width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(135deg, #5b7fef, #7c3aed); display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 28px rgba(91,127,239,0.4); animation: xcFloat 4s ease-in-out infinite; }
    @keyframes xcFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
    .xcw-welcome-title { font-size: 17px; font-weight: 700; color: #1e2d50; margin: 0; }
    .xcw-welcome-sub { font-size: 13px; color: #8b96af; margin: 0; max-width: 270px; line-height: 1.6; }
    .xcw-chips { display: flex; flex-wrap: wrap; gap: 7px; justify-content: center; }
    .xcw-chip { background: #fff; border: 1px solid #d0d8f0; border-radius: 999px; padding: 6px 14px; font-size: 12px; color: #4f7ef7; cursor: pointer; transition: all 0.14s; }
    .xcw-chip:hover { background: #4f7ef7; color: #fff; border-color: #4f7ef7; }

    .xcw-skels { padding: 12px 16px; display: flex; flex-direction: column; gap: 12px; }
    .xcw-skel { height: 56px; border-radius: 14px; background: linear-gradient(90deg, #f0f4fc 25%, #e8edf5 50%, #f0f4fc 75%); background-size: 200% 100%; animation: xcShimmer 1.5s infinite; }
    .xcw-skel--user { align-self: flex-end; width: 58%; }
    .xcw-skel--ai { align-self: flex-start; width: 72%; }
    .xcw-skel--sm { width: 36%; }
    .xcw-skel--lg { height: 80px; width: 80%; }
    @keyframes xcShimmer { to { background-position: -200% 0; } }
  `]
})
export class ChatWindowComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scrollAnchor') scrollAnchor!: ElementRef;

  // Flat primitive/array fields — Angular Default detection sees these directly
  activeMessages:    ChatMessage[] = [];
  activeSessionId:   string | null = null;
  activeSession:     ChatSession | null = null;   // full session object (for tokens)
  activeSessionTitle = 'XEDGE AI Assistant';
  activeControllerId = '';
  isStreaming        = false;
  sidebarOpen        = false;
  loadingMessages    = false;
  errorMsg:          string | null = null;
  isFullscreen       = false;

  private sub!: Subscription;
  private shouldScroll = false;

  suggestions = [
    'List all connected devices',
    'Show devices needing attention',
    'What are the latest telemetry alerts?',
    'Which devices are offline?',
    "Show today's test results",
    'Summarize recent RF test failures',
  ];

  constructor(
    public chatState: ChatStateService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    // Subscribe and unpack every field we need explicitly
    // so Angular Default detection sees primitive/array changes
    this.sub = this.chatState.state$.subscribe((s: ChatState) => {
      this.zone.run(() => {
        // Always reassign activeMessages to a NEW array reference
        // so *ngFor picks up every delta change
        const sid = s.activeSessionId;
        this.activeMessages = sid
          ? [...(s.messages[sid] || [])]
          : [];

        this.activeSessionId    = s.activeSessionId;
        this.activeSession      = s.sessions.find(x => x.id === sid) ?? null;
        this.activeSessionTitle = this.activeSession?.title || 'XEDGE AI Assistant';
        this.activeControllerId = s.activeControllerId;
        this.isStreaming        = s.isStreaming;
        this.sidebarOpen        = s.isSidebarOpen;
        this.loadingMessages    = s.isLoadingMessages;
        this.errorMsg           = s.error;
        this.isFullscreen       = s.isFullscreen;
        this.shouldScroll       = true;

        this.cdr.detectChanges(); // force sync update — don't wait for next tick
      });
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  private scrollToBottom(): void {
    try {
      this.scrollAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
    } catch {}
  }

  trackById(_: number, m: ChatMessage): string { return m.id; }

  toggleSidebar():    void { this.chatState.toggleSidebar(); }
  toggleFullscreen(): void { this.chatState.toggleFullscreen(); }
  newChat():          void { this.chatState.createNewSession(); }
  sendSuggestion(text: string): void { this.chatState.sendMessage(text); }

  compact(): void {
    if (!this.activeSessionId) return;
    if (confirm('Compact this session? History will be summarised into a single message to free context tokens.')) {
      this.chatState.compactSession(this.activeSessionId);
    }
  }

  resetUsage(): void {
    if (!this.activeSessionId) return;
    this.chatState.resetSessionUsage(this.activeSessionId);
  }

  /** Format number with commas: 134870 → "134,870" */
  formatNum(n: number): string {
    return (n ?? 0).toLocaleString();
  }
  onSend(evt: SendEvent): void { this.chatState.sendMessage(evt.text, evt.attachments); }

  onFeedback(evt: FeedbackEvent): void {
    this.chatState.submitFeedback(evt.msgId, evt.verdict, evt.correction);
  }

  onToolToggle(evt: ToolToggleEvent): void {
    if (this.activeSessionId) {
      this.chatState.toggleToolExpand(this.activeSessionId, evt.msgId, evt.toolId);
    }
  }
}